import { DeleteOutlined, DownloadOutlined, DownOutlined, EditOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Dropdown, Input, message, Modal, Select, Skeleton, Space, Switch, Table, Tooltip } from "antd"
import { useState, useContext, Fragment } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { UserContext } from "../../context"
import { isAdmin } from "../../utils/userUtils"

import { ExclamationCircleFilled } from "@ant-design/icons"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTheme } from "styled-components"
import { ContainerFull, LabeledBox, NameValueRow, OrderComments, ViewSampleSequence } from "../../components"
import { ORDER_STATUS_COLORS, SAMPLE_STATUS_COLORS, ShowMessagePage } from "../../config"
import { addOrderComment, batchDeleteOrderResults, completeRunIfSingleOrder, deleteOrderResult, downloadOrderResultJson, downloadOrderResultZip, getInvoiceDownloadLogs, getOrderEmailLog, getOrderResultDownloadLogs, getOrderResults, getOrders, regenerateOrderSheet, releaseOrderResults, sendResultReadyEmail, setPinnedOrderResult, updateOrderBillingType, updateOrderItrPurityAnalysis, updateOrderPaidStatus, updateOrderStatus, updateOrderSubmissionType } from "../../endpoints"
import { createInvoice } from "../../endpoints/invoiceEndpoints"
import { BillingType, OrderCommentType, OrderStatusType } from "../../types"

const MENU_WITH_COLORS = false
export function AdminOrderDetail(): JSX.Element {
    const user = useContext(UserContext)
    const navigate = useNavigate()
    const theme = useTheme()
    const { orderId } = useParams()
    const queryClient = useQueryClient()
    const [messageApi, contextHolder] = message.useMessage()
    const [modal, modalContextHolder] = Modal.useModal()
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [isReleaseInOneStepLoading, setIsReleaseInOneStepLoading] = useState(false)

    // Fetch order result download logs (must be inside component to access orderId)
    const {
        data: downloadLogData,
        isPending: isDownloadLogPending,
        
    } = useQuery({
        queryKey: ["getOrderResultDownloadLogs", orderId],
        queryFn: () => getOrderResultDownloadLogs({ orderId: parseInt(orderId ?? "0") }),
        refetchOnMount: "always",
        enabled: !!orderId,
    })

    // console.log("orderId is ", orderId)
    // const [orderDetails, setOrderDetails] = useState<OrderInfoInterface>()
    // const [shippingDetails,setShippingDetails]=useState<string>('')
    const {
        isPending: isOrderPending,
        isError: isOrderError,
        data: orderData,
        error: orderError
    } = useQuery({
        queryKey: ["getorders", "one", orderId],
        queryFn: () => getOrders({ type: "one", orderIds: [parseInt(orderId ?? "0")] }),
        refetchOnMount: "always",
    })

    // Fetch order email logs
    const {
        data: emailLogData,
        isPending: isEmailLogPending,
    } = useQuery({
        queryKey: ["getOrderEmailLog", orderId],
        queryFn: () => getOrderEmailLog({ orderId: parseInt(orderId ?? "0") }),
        refetchOnMount: "always",
        enabled: !!orderId,
    })

    // Fetch invoice download logs
    const {
        data: invoiceDownloadLogData,
        isPending: isInvoiceDownloadLogPending,
    } = useQuery({
        queryKey: ["getInvoiceDownloadLogs", orderId],
        queryFn: () => getInvoiceDownloadLogs({ orderId: parseInt(orderId ?? "0") }),
        refetchOnMount: "always",
        enabled: !!orderId,
    })

    // Fetch order results
    const {
        data: orderResultsData,
        isPending: isOrderResultsPending,
    } = useQuery({
        queryKey: ["getOrderResults", orderId],
        queryFn: () => getOrderResults({ orderId: parseInt(orderId ?? "0") }),
        refetchOnMount: "always",
        enabled: !!orderId,
    })

    // Release order results mutation
    const releaseOrderResultsMutation = useMutation({
        mutationFn: releaseOrderResults,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(data.message)
                // Refetch order results to update the table
                queryClient.invalidateQueries({ queryKey: ["getOrderResults", orderId] })
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to release order results")
            console.error("Release error:", error)
        }
    })

    // Delete order results mutation
    const deleteOrderResultMutation = useMutation({
        mutationFn: deleteOrderResult,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(data.message)
                // Refetch order results to update the table
                queryClient.invalidateQueries({ queryKey: ["getOrderResults", orderId] })
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to delete order result")
            console.error("Delete error:", error)
        }
    })

    // Batch delete order results mutation
    const batchDeleteOrderResultsMutation = useMutation({
        mutationFn: batchDeleteOrderResults,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(`Batch delete completed: ${data.details.successfulDeletes}/${data.details.totalRequested} results deleted`)
                // Clear selection
                setSelectedRowKeys([])
                // Refetch order results to update the table
                queryClient.invalidateQueries({ queryKey: ["getOrderResults", orderId] })
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to batch delete order results")
            console.error("Batch delete error:", error)
        }
    })

    const setPinnedOrderResultMutation = useMutation({
        mutationFn: setPinnedOrderResult,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(data.message)
                queryClient.invalidateQueries({ queryKey: ["getOrderResults", orderId] })
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to update pinned result")
            console.error("Set pinned result error:", error)
        }
    })

    // Regenerate order sheet mutation
    const regenerateOrderSheetMutation = useMutation({
        mutationFn: regenerateOrderSheet,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(data.message)
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to regenerate order sheet")
            console.error("Regenerate order sheet error:", error)
        }
    })

    // Update submission type mutation
    const updateSubmissionTypeMutation = useMutation({
        mutationFn: updateOrderSubmissionType,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(data.message)
                // Refetch orders to update the display
                queryClient.invalidateQueries({ queryKey: ["getorders", "one", orderId] })
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to update submission type")
            console.error("Update submission type error:", error)
        }
    })

    const updateBillingTypeMutation = useMutation({
        mutationFn: updateOrderBillingType,
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success(data.message)
                queryClient.invalidateQueries({ queryKey: ["getorders", "one", orderId] })
                queryClient.invalidateQueries({ queryKey: ["getorders", "all"] })
            } else {
                messageApi.error(data.message)
            }
        },
        onError: (error) => {
            messageApi.error("Failed to update billing type")
            console.error("Update billing type error:", error)
        }
    })

    // Batch delete handler
    const handleBatchDelete = () => {
        if (selectedRowKeys.length === 0) {
            messageApi.warning("Please select at least one order result to delete")
            return
        }

        const selectedPinnedCount = (orderResultsData?.results ?? []).filter((result: any) =>
            result.pin_for_download && selectedRowKeys.includes(result.id)
        ).length

        if (selectedPinnedCount > 0) {
            messageApi.warning("Pinned result cannot be deleted. Unpin it first.")
            return
        }

        // First, prompt for password
        let passwordInput = ''
        modal.confirm({
            title: 'Enter Password',
            icon: <ExclamationCircleFilled />,
            content: (
                <div>
                    <p>Please enter the passcode to delete {selectedRowKeys.length} order result(s):</p>
                    <Input.Password
                        autoFocus
                        placeholder="Enter password"
                        onChange={(e) => {
                            passwordInput = e.target.value
                        }}
                        onPressEnter={() => {
                            // Trigger the OK button action
                            const okButton = document.querySelector('.ant-modal .ant-btn-primary') as HTMLButtonElement
                            okButton?.click()
                        }}
                    />
                </div>
            ),
            okText: 'Verify',
            cancelText: 'Cancel',
            onOk() {
                if (passwordInput !== 'plabdd') {
                    messageApi.error('Incorrect password. Delete operation cancelled.')
                    return
                }
                batchDeleteOrderResultsMutation.mutate({ 
                    orderResultIds: selectedRowKeys.map(key => Number(key)) 
                })
               
            },
        })
    }

    const handleCompareResults = () => {
        if (!orderId) return
        if (selectedRowKeys.length < 2) {
            messageApi.warning("Please select at least 2 order results to compare")
            return
        }

        const ids = selectedRowKeys.map((key) => Number(key)).join(",")
        navigate(`/admin/order/${orderId}/compare-results?ids=${ids}`)
    }

    // console.log("emailLogData", emailLogData)
    if (isOrderPending) {
        return <Skeleton />
    }

    if (isOrderError) {
        return ShowMessagePage("Error", orderError?.message ?? "unknown error")
    }

    if (orderData.code !== 1000) {
        return ShowMessagePage("Error", orderData.message)
    }

    const selectedPinnedCount = (orderResultsData?.results ?? []).filter((result: any) =>
        result.pin_for_download && selectedRowKeys.includes(result.id)
    ).length

    const sendResultReadyEmailAction = async (oid: number) => {
        const result = await sendResultReadyEmail({ orderId: oid })
        if (result.code === 1000) {
            messageApi.success("Result ready email sent successfully!")
            queryClient.invalidateQueries({
                queryKey: ["getOrderEmailLog", orderId],
            })
            return true
        }
        // this is not good, by pass the email step will cause the result be released in the dev mode
        // else if (result.code === 200) {
        //     messageApi.warning( "DEV MODE, no email is sent")

        //     return true
        // }
        messageApi.error(result.message || "Failed to send email")
        return false
    }

    const handleReleaseInOneStep = async () => {
        if (!orderDetails?.id) return

        const results = orderResultsData?.results ?? []
        if (results.length !== 1) {
            messageApi.error("Release in one click requires exactly one result in the table")
            return
        }

        const singleResult = results[0]
        const awsRequired = !!(orderDetails?.user?.aws_profile && orderDetails.user.aws_profile.trim() !== "")
        const apiRequired = orderDetails?.user?.api_key === "yes"
        const awsAlreadyPushed = !!(singleResult?.pushed_group_name && singleResult.pushed_group_name.trim() !== "")
        const apiAlreadyReleased = !!(singleResult?.released_group_name && singleResult.released_group_name.trim() !== "")
        const hasAnyReleaseConfig = awsRequired || apiRequired
        const allActionsCompleted =
            hasAnyReleaseConfig && (!awsRequired || awsAlreadyPushed) && (!apiRequired || apiAlreadyReleased)

        if (allActionsCompleted) {
            messageApi.error("Cannot run one-step release: the result has already been released")
            return
        }

        const emailAlreadySent = (emailLogData?.emails ?? []).some((email: any) => {
            const subject = (email?.subject || "").toLowerCase()
            return subject.includes("result") && subject.includes("ready")
        })
        if (emailAlreadySent) {
            messageApi.error("Cannot run one-step release: result ready email has already been sent")
            return
        }

        if (!hasAnyReleaseConfig) {
            messageApi.error("Cannot run one-step release: no AWS/API release configuration found")
            return
        }

        setIsReleaseInOneStepLoading(true)
        try {
            const isCompletedStatus = ["completed", "rerun-completed", "review-completed"].includes(
                orderDetails.status || ""
            )

            if (!isCompletedStatus) {
                const updateResult = await updateOrderStatus({
                    orderId: parseInt(orderId ?? "0"),
                    newStatus: "completed",
                })
                if (updateResult.code !== 1000) {
                    messageApi.error(updateResult.message || "Failed to update order status to completed")
                    return
                }
                queryClient.invalidateQueries({ queryKey: ["getorders", "one", orderId] })
            }

            const completeRunResult = await completeRunIfSingleOrder({ orderId: orderDetails.id })
            if (completeRunResult.code !== 1000) {
                messageApi.error(completeRunResult.message || "Failed to update run status")
                return
            }

            const emailSent = await sendResultReadyEmailAction(orderDetails.id)
            if (!emailSent) return

            const releaseResult = await releaseOrderResultsMutation.mutateAsync({ orderResultId: singleResult.id })
            if (releaseResult?.code !== 1000) {
                messageApi.error(releaseResult?.message || "Failed to release order result")
                return
            }

            messageApi.success("Release in one click completed")
            queryClient.invalidateQueries({ queryKey: ["getOrderResults", orderId] })
        } catch (error) {
            console.error("Release in one click error:", error)
            messageApi.error("Failed to complete one-click release")
        } finally {
            setIsReleaseInOneStepLoading(false)
        }
    }

    const orders = orderData.orders
    let orderDetails = orders?.[0]
    let shippingDetails: string = ""
    if (orders && orders.length > 0) {
        const odetails = orders[0]
        let sdetails = "Method: " + odetails?.shippinginfo?.method
        if (odetails?.shipped) {
            if (odetails?.shippinginfo?.method === "dropbox") {
                sdetails += ": " + odetails.shippinginfo.dropbox?.label + ", samples dropped off"
            } else {
                sdetails += ", samples shipped"
            }
        } else {
            if (odetails?.shippinginfo?.method === "dropbox") {
                sdetails += ": " + odetails.shippinginfo.dropbox?.label + ", samples not dropped off"
            } else {
                sdetails += ", samples not shipped"
            }
        }
        if (odetails?.shippinginfo?.method === "pickup") {
            sdetails +=
                ", address: " +
                odetails?.shippinginfo.pickupinfo?.line1 +
                " " +
                odetails?.shippinginfo.pickupinfo?.line2 +
                " " +
                odetails?.shippinginfo.pickupinfo?.city
        }
        // setOrderDetails(odetails)
        // setShippingDetails(sdetails)
        shippingDetails = sdetails
    }

    // console.log(orderDetails)

    return (
        <>
            {contextHolder}
            {modalContextHolder}
            <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
                <div style={{ display: 'flex', gap: '12px', marginBottom: 16 }}>
                    <Button onClick={() => navigate(-1)}>
                        &lt; Back
                    </Button>
                    <Button 
                        type="default"
                        icon={<ReloadOutlined />}
                        loading={regenerateOrderSheetMutation.isPending}
                        onClick={() => {
                            Modal.confirm({
                                title: 'Regenerate Order Sheet',
                                icon: <ExclamationCircleFilled />,
                                content: 'Are you sure you want to regenerate the order sheet? This will overwrite the existing PDF with updated order information.',
                                onOk() {
                                    regenerateOrderSheetMutation.mutate({ orderId: parseInt(orderId ?? "0") })
                                }
                            })
                        }}
                    >
                        Regenerate Order Sheet
                    </Button>
                </div>

                <LabeledBox title={`Order ${orderDetails?.id}, Service: ${orderDetails?.productDetails?.name}, Samples: ${orderDetails?.samples?.length || 0}`}>
                    <NameValueRow
                        key="processingrequirements"
                        Name={"Processing Requirements"}
                        Value={
                            <div style={{ width: "100%" }}>
                                {orderDetails?.user ? (
                                    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ fontWeight: "bold" }}>AWS Push:</span>
                                            <span style={{ 
                                                color: (orderDetails.user.aws_profile && orderDetails.user.aws_profile.trim() !== '') ? "#52c41a" : "#d9d9d9",
                                                fontWeight: (orderDetails.user.aws_profile && orderDetails.user.aws_profile.trim() !== '') ? "bold" : "normal"
                                            }}>
                                                {(orderDetails.user.aws_profile && orderDetails.user.aws_profile.trim() !== '') ? "Required" : "Not Required"}
                                            </span>
                                            {(orderDetails.user.aws_profile && orderDetails.user.aws_profile.trim() !== '') && (
                                                <span style={{ color: "#8c8c8c", fontSize: "12px" }}>
                                                    (Profile: {orderDetails.user.aws_profile}
                                                    {orderDetails.user.aws_bucket_name && `, Bucket: ${orderDetails.user.aws_bucket_name}`})
                                                </span>
                                            )}
                                        </div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ fontWeight: "bold" }}>API Release:</span>
                                            <span style={{ 
                                                color: orderDetails.user.api_key === 'yes' ? "#52c41a" : "#d9d9d9",
                                                fontWeight: orderDetails.user.api_key === 'yes' ? "bold" : "normal"
                                            }}>
                                                {orderDetails.user.api_key === 'yes' ? "Required" : "Not Required"}
                                            </span>
                                            {orderDetails.user.api_key === 'yes' && (
                                                <span style={{ color: "#8c8c8c", fontSize: "12px" }}>
                                                    (Configured)
                                                </span>
                                            )}
                                        </div>
                                        {(orderDetails.user.group_name && orderDetails.user.group_name.trim() !== '') && (
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ fontWeight: "bold" }}>Group:</span>
                                                <span style={{ color: "#1890ff" }}>
                                                    {orderDetails.user.group_name}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <span style={{ color: "#bbb" }}>No user information available</span>
                                )}
                            </div>
                        }
                    />
                    <NameValueRow
                        key="submissiontype"
                        Name={"Submission Type"}
                        Value={
                            <div style={{ width: "100%" }}>
                                <Select
                                    value={orderDetails?.submission_type || 'unknown'}
                                    style={{ width: 200 }}
                                    onChange={(value) => {
                                        if (!orderId) return
                                        updateSubmissionTypeMutation.mutate({
                                            orderId: parseInt(orderId),
                                            submissionType: value
                                        })
                                    }}
                                    options={[
                                        { value: 'unknown', label: 'Unknown' },
                                        { value: 'tubes', label: 'Tubes' },
                                        { value: 'plate-column-order', label: 'Plate (Column Order)' },
                                        { value: 'plate-row-order', label: 'Plate (Row Order)' },
                                        { value: 'plate-map', label: 'Plate (Custom Map)' }
                                    ]}
                                    loading={updateSubmissionTypeMutation.isPending}
                                />
                            </div>
                        }
                    />
                    {isAdmin(user.user) && (
                        <NameValueRow
                            key="billingtype"
                            Name={"Billing Type"}
                            Value={
                                <div style={{ width: "100%" }}>
                                    <Select
                                        value={(orderDetails?.billing_type || "normal") as BillingType}
                                        style={{ width: 200 }}
                                        variant="borderless"
                                        onChange={(value: BillingType) => {
                                            if (!orderId) return
                                            updateBillingTypeMutation.mutate({
                                                orderId: parseInt(orderId),
                                                billingType: value,
                                            })
                                        }}
                                        options={[
                                            { value: "normal", label: "normal" },
                                            { value: "internal", label: <span style={{ backgroundColor: "#b7eb8f", color: "#135200", padding: "1px 6px", borderRadius: 4 }}>internal</span> },
                                            { value: "free rerun", label: <span style={{ backgroundColor: "#ffd591", color: "#873800", padding: "1px 6px", borderRadius: 4 }}>free rerun</span> },
                                        ]}
                                        loading={updateBillingTypeMutation.isPending}
                                    />
                                </div>
                            }
                        />
                    )}
                    <NameValueRow
                        key="orderresults"
                        Name={"Order Results"}
                        Value={
                            <div style={{ width: "100%" }}>
                                {isOrderResultsPending ? (
                                    <Skeleton active paragraph={{ rows: 2 }} />
                                ) : orderResultsData?.code === 1000 && orderResultsData.results?.length > 0 ? (
                                    <div>
                                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ color: '#666' }}>
                                                {selectedRowKeys.length > 0 ? `${selectedRowKeys.length} result(s) selected` : 'No results selected'}
                                            </span>
                                            <Space>
                                                <Button
                                                    onClick={() => {
                                                        const htmlUrl = `/api/orders/result-html/${orderId}`
                                                        window.open(htmlUrl, '_blank')
                                                    }}
                                                >
                                                    Show Most Recent Result HTML
                                                </Button>
                                                <Button
                                                    onClick={handleCompareResults}
                                                    disabled={selectedRowKeys.length < 2}
                                                >
                                                    Compare Results
                                                </Button>
                                                <Button
                                                    type="primary"
                                                    danger
                                                    disabled={selectedRowKeys.length === 0 || selectedPinnedCount > 0}
                                                    loading={batchDeleteOrderResultsMutation.isPending}
                                                    onClick={handleBatchDelete}
                                                    icon={<DeleteOutlined />}
                                                >
                                                    Batch Delete ({selectedRowKeys.length})
                                                </Button>
                                            </Space>
                                        </div>
                                        <Table
                                            dataSource={orderResultsData.results}
                                            size="small"
                                            pagination={false}
                                            rowKey="id"
                                            rowSelection={{
                                                selectedRowKeys,
                                                onChange: (selectedRowKeys: React.Key[]) => {
                                                    setSelectedRowKeys(selectedRowKeys)
                                                },
                                                getCheckboxProps: (record: any) => ({
                                                    name: record.id,
                                                }),
                                            }}
                                        columns={[
                                            
                                            {
                                                title: "ID",
                                                dataIndex: "id",
                                                key: "id",
                                                width: "6%",
                                            },
                                            {
                                                title: "Pin for Download",
                                                dataIndex: "pin_for_download",
                                                key: "pin_for_download",
                                                width: "10%",
                                                render: (_: boolean, record: any) => (
                                                    <Switch
                                                        checked={!!record.pin_for_download}
                                                        checkedChildren="Pinned"
                                                        unCheckedChildren=""
                                                        loading={setPinnedOrderResultMutation.isPending}
                                                        onChange={(checked) => {
                                                            if (!checked || record.pin_for_download) {
                                                                return
                                                            }
                                                            setPinnedOrderResultMutation.mutate({ orderResultId: record.id })
                                                        }}
                                                    />
                                                ),
                                            },
                                            {
                                                title: "JSON File",
                                                dataIndex: "jsonfile",
                                                key: "jsonfile",
                                                width: "20%",
                                                render: (text: string, record: any) => {
                                                    if (!text) return "-"
                                                    const url = `/api/download/orderresult/json/${record.id}?r=${Math.random()}`
                                                    return (
                                                        <Link to={url} target="_blank" rel="noopener noreferrer">
                                                            {text}
                                                        </Link>
                                                    )
                                                },
                                            },
                                            {
                                                title: "ZIP File",
                                                dataIndex: "zipfile",
                                                key: "zipfile",
                                                width: "20%",
                                                render: (text: string, record: any) => {
                                                    if (!text) return "-"
                                                    const url = `/api/download/orderresult/zip/${record.id}?r=${Math.random()}`
                                                    return (
                                                        <Link to={url} target="_blank" rel="noopener noreferrer">
                                                            {text}
                                                        </Link>
                                                    )
                                                },
                                            },
                                            {
                                                title: "ZIP File Size",
                                                dataIndex: "zipfilesize",
                                                key: "zipfilesize",
                                                width: "10%",
                                                render: (size: number | null, record: any) => {
                                                    if (!record?.zipfile) return "-"
                                                    if (size == null || size < 0) return "-"
                                                    return String(size)
                                                },
                                            },
                                            {
                                                title: "Pushed to AWS",
                                                dataIndex: "pushed_group_name",
                                                key: "pushed_group_name",
                                                width: "12%",
                                                render: (text: string) => text || "-",
                                            },
                                            {
                                                title: "Released to API",
                                                dataIndex: "released_group_name",
                                                key: "released_group_name",
                                                width: "12%",
                                                render: (text: string) => text || "-",
                                            },
                                            {
                                                title: "Notes",
                                                dataIndex: "notes",
                                                key: "notes",
                                                width: "15%",
                                                render: (text: string) => {
                                                    if (!text || text.trim() === "") return "-"
                                                    
                                                    // Truncate long notes
                                                    const maxLength = 50
                                                    const displayText = text.length > maxLength 
                                                        ? text.substring(0, maxLength) + "..." 
                                                        : text
                                                    
                                                    return (
                                                        <Tooltip title={text} placement="topLeft">
                                                            <span style={{ cursor: "pointer" }}>{displayText}</span>
                                                        </Tooltip>
                                                    )
                                                },
                                            },
                                            {
                                                title: "Created",
                                                dataIndex: "ts",
                                                key: "ts",
                                                width: "15%",
                                                render: (ts: string) => new Date(ts).toLocaleString(),
                                            },
                                            {
                                                title: "Actions",
                                                key: "action",
                                                width: "20%",
                                                render: (_, record: any) => {
                                                    const awsRequired = orderDetails?.user?.aws_profile && orderDetails.user.aws_profile.trim() !== ''
                                                    const apiRequired = orderDetails?.user?.api_key === 'yes'
                                                    const awsAlreadyPushed = record.pushed_group_name && record.pushed_group_name.trim() !== ''
                                                    const apiAlreadyReleased = record.released_group_name && record.released_group_name.trim() !== ''
                                                    const isPinnedForDownload = !!record.pin_for_download

                                                    const needsAction = (awsRequired && !awsAlreadyPushed) || (apiRequired && !apiAlreadyReleased)
                                                    const hasAnyReleaseConfig = awsRequired || apiRequired
                                                    const allActionsCompleted = hasAnyReleaseConfig && (!awsRequired || awsAlreadyPushed) && (!apiRequired || apiAlreadyReleased)

                                                    const isReleaseLoading = releaseOrderResultsMutation.isPending
                                                    const isDeleteLoading = deleteOrderResultMutation.isPending

                                                    let buttonText = "Release"
                                                    if (!hasAnyReleaseConfig) {
                                                        buttonText = "No Config"
                                                    } else if (allActionsCompleted) {
                                                        buttonText = "Released"
                                                    }

                                                    return (
                                                        <Space direction="horizontal" size="small">
                                                            <Tooltip title="Edit order result">
                                                                <Button
                                                                    type="default"
                                                                    size="large"
                                                                    icon={<EditOutlined />}
                                                                    onClick={() => {
                                                                        navigate(`/admin/orderresult/${record.id}/edit?orderId=${orderId}`)
                                                                    }}
                                                                >
                                                                    Edit

                                                                </Button>
                                                            </Tooltip>
                                                            <Button
                                                                type="default"
                                                                size="large"
                                                                disabled={!needsAction || isReleaseLoading || isDeleteLoading}
                                                                loading={isReleaseLoading}
                                                                onClick={() => {
                                                                    Modal.confirm({
                                                                        title: 'Release Order Results',
                                                                        icon: <ExclamationCircleFilled />,
                                                                        content: (
                                                                            <div>
                                                                                <p>This will process the order results based on user group settings:</p>
                                                                                {awsRequired && !awsAlreadyPushed && (
                                                                                    <div>
                                                                                        <p>• Push files to AWS S3:</p>
                                                                                        <p style={{ marginLeft: '16px' }}>
                                                                                            Profile: <strong>{orderDetails?.user?.aws_profile}</strong>
                                                                                        </p>
                                                                                        <p style={{ marginLeft: '16px' }}>
                                                                                            Bucket: <strong>{orderDetails?.user?.aws_bucket_name || 'Not configured'}</strong>
                                                                                        </p>
                                                                                    </div>
                                                                                )}
                                                                                {apiRequired && !apiAlreadyReleased && (
                                                                                    <p>• Release for REST API access</p>
                                                                                )}
                                                                                {(!awsRequired || awsAlreadyPushed) && (!apiRequired || apiAlreadyReleased) && (
                                                                                    <p>• No action needed (already processed or not required)</p>
                                                                                )}
                                                                            </div>
                                                                        ),
                                                                        onOk() {
                                                                            releaseOrderResultsMutation.mutate({ orderResultId: record.id })
                                                                        },
                                                                    })
                                                                }}
                                                            >
                                                                {buttonText}
                                                            </Button>

                                                            <Tooltip title={isPinnedForDownload ? "Pinned result cannot be deleted" : "Delete order result"}>
                                                                <Button
                                                                    type="default"
                                                                    size="large"
                                                                    danger
                                                                    disabled={isPinnedForDownload || isReleaseLoading || isDeleteLoading}
                                                                    loading={isDeleteLoading}
                                                                    
                                                                    onClick={() => {
                                                                        let passwordInput = ''
                                                                        modal.confirm({
                                                                            title: 'Enter Password',
                                                                            icon: <ExclamationCircleFilled />,
                                                                            content: (
                                                                                <div>
                                                                                    <p>Please enter the passcode to delete this order result:</p>
                                                                                    <Input.Password
                                                                                        autoFocus
                                                                                        placeholder="Enter password"
                                                                                        onChange={(e) => {
                                                                                            passwordInput = e.target.value
                                                                                        }}
                                                                                        onPressEnter={() => {
                                                                                            const okButton = document.querySelector('.ant-modal .ant-btn-primary') as HTMLButtonElement
                                                                                            okButton?.click()
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            ),
                                                                            okText: 'Verify',
                                                                            cancelText: 'Cancel',
                                                                            onOk() {
                                                                                if (passwordInput !== 'plabdd') {
                                                                                    messageApi.error('Incorrect password. Delete operation cancelled.')
                                                                                    return
                                                                                }

                                                                                deleteOrderResultMutation.mutate({ orderResultId: record.id })
                                                                            },
                                                                        })
                                                                    }}
                                                                >Delete</Button>
                                                            </Tooltip>
                                                        </Space>
                                                    )
                                                },
                                            }
                                        ]}
                                        style={{ marginTop: 8 }}
                                    />
                                    </div>
                                ) : (
                                    <span style={{ color: "#bbb" }}>No order results found</span>
                                )}
                            </div>
                        }
                    />
                    
                    <NameValueRow
                        key="orderemaillog"
                        Name={"Email Log"}
                        Value={
                            <div style={{ width: "100%" }}>
                                {isEmailLogPending ? (
                                    <Skeleton active paragraph={{ rows: 2 }} />
                                ) : emailLogData?.code === 1000 && emailLogData.emails?.length > 0 ? (
                                    <Table
                                        dataSource={emailLogData.emails}
                                        size="small"
                                        pagination={false}
                                        rowKey="id"
                                        columns={[
                                            {
                                                title: "Subject",
                                                dataIndex: "subject",
                                                key: "subject",
                                                width: "60%",
                                            },
                                            {
                                                title: "Time",
                                                dataIndex: "ts",
                                                key: "ts",
                                                width: "40%",
                                                render: (ts: string) => new Date(ts).toLocaleString(),
                                            },
                                        ]}
                                        style={{ marginTop: 8 }}
                                    />
                                ) : (
                                    <span style={{ color: "#bbb" }}>No emails sent</span>
                                )}
                                <div style={{ marginTop: "1em", marginBottom: "1em" }}>
                                    <Space>
                                        <Button
                                            type="default"
                                            disabled={
                                                !orderDetails?.status ||
                                                !["completed", "rerun-completed", "review-completed"].includes(
                                                    orderDetails.status
                                                )
                                            }
                                            onClick={async () => {
                                                if (!orderDetails?.id) return
                                                try {
                                                    await sendResultReadyEmailAction(orderDetails.id)
                                                } catch (error) {
                                                    console.error("Error sending result ready email:", error)
                                                    messageApi.error("Failed to send email")
                                                }
                                            }}
                                        >
                                            📧 Send Result Ready Email
                                        </Button>
                                        <Button
                                            type="primary"
                                            loading={isReleaseInOneStepLoading}
                                            onClick={handleReleaseInOneStep}
                                        >
                                            Release in one click
                                        </Button>
                                    </Space>
                                </div>
                            </div>
                        }
                    />

                    {isAdmin(user.user) && (
                        <NameValueRow
                            key="invoicedownloadlog"
                            Name={"Invoice Download Log"}
                            Value={
                                <div style={{ width: "100%" }}>
                                    {isInvoiceDownloadLogPending ? (
                                        <Skeleton active paragraph={{ rows: 2 }} />
                                    ) : invoiceDownloadLogData?.code === 0 && invoiceDownloadLogData.downloads?.length > 0 ? (
                                        <Table
                                            dataSource={invoiceDownloadLogData.downloads}
                                            size="small"
                                            pagination={false}
                                            rowKey="id"
                                            columns={[
                                                {
                                                    title: "User",
                                                    dataIndex: "username",
                                                    key: "username",
                                                    width: "40%",
                                                },
                                                {
                                                    title: "Action",
                                                    dataIndex: "action",
                                                    key: "action",
                                                    width: "40%",
                                                    render: (action: string) => {
                                                        if (action.includes('invoiceId=')) {
                                                            const invoiceIdMatch = action.match(/invoiceId=(\d+)/)
                                                            return invoiceIdMatch ? `Downloaded Invoice #${invoiceIdMatch[1]}` : 'Downloaded Invoice'
                                                        }
                                                        return 'Downloaded Invoice'
                                                    },
                                                },
                                                {
                                                    title: "Time",
                                                    dataIndex: "ts",
                                                    key: "ts",
                                                    width: "20%",
                                                    render: (ts: string) => new Date(ts).toLocaleString(),
                                                },
                                            ]}
                                            style={{ marginTop: 8 }}
                                        />
                                    ) : (
                                        <span style={{ color: "#bbb" }}>No invoice downloads</span>
                                    )}
                                </div>
                            }
                        />
                    )}

                    <NameValueRow key="name" Name={"Order name"} Value={orderDetails?.ordername ?? "None"} />

                    <NameValueRow 
                        key="user" 
                        Name={"Placed by"} 
                        Value={
                            orderDetails?.user ? (
                                <span>
                                    {orderDetails.user.firstName} {orderDetails.user.lastName} ({orderDetails.user.email})
                                </span>
                            ) : (
                                "Unknown user"
                            )
                        } 
                    />

                    <NameValueRow
                        key="status"
                        Name={"Status"}
                        Value={
                            <Space direction="horizontal" size="small" style={{ width: "100%" }}>
                                <Dropdown
                                    menu={{
                                        items: Object.entries(ORDER_STATUS_COLORS).map(([status, color]) => ({
                                            key: status,
                                            label: (
                                                <span
                                                    style={{
                                                        display: "block",
                                                        backgroundColor: MENU_WITH_COLORS ? color : "white",
                                                        color: "#222",
                                                        padding: "4px 12px",
                                                        width: "100%",
                                                    }}
                                                >
                                                    {status || "Unknown"}
                                                </span>
                                            ),
                                            style: { padding: 0 },
                                        })),
                                        onClick: async (info) => {
                                            const key = info.key as OrderStatusType

                                            if (key === orderDetails?.status) return // No change
                                            const result = await updateOrderStatus({
                                                orderId: parseInt(orderId ?? "0"),
                                                newStatus: key,
                                            })
                                            if (result.code === 1000) {
                                                messageApi.success(`Order status updated to ${key}`)
                                                queryClient.invalidateQueries({ queryKey: ["getorders"] })
                                                navigate(`/admin/order/${orderId}`)
                                            } else {
                                                messageApi.error(result.message)
                                            }
                                        },
                                    }}
                                    trigger={["click"]}
                                >
                                    <Button
                                        size="small"
                                        style={{
                                            backgroundColor: ORDER_STATUS_COLORS[orderDetails?.status ?? ""],
                                            color: "#222",
                                            minWidth: 120,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            position: "relative",
                                        }}
                                    >
                                        <span
                                            style={{
                                                flex: 1,
                                                textAlign: "center",
                                                pointerEvents: "none",
                                                marginRight: 24,
                                                whiteSpace: "nowrap",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                            }}
                                        >
                                            {orderDetails?.status ?? ""}
                                        </span>
                                        <span
                                            style={{
                                                position: "absolute",
                                                right: 8,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            <DownOutlined />
                                        </span>
                                    </Button>
                                </Dropdown>
                                {(orderDetails?.status === "completed" ||
                                    orderDetails?.status === "rerun-completed" ||
                                    orderDetails?.status === "review-requested" ||
                                    orderDetails?.status === "review-completed") && (
                                    <Tooltip title="Download results">
                                        <a
                                            href={`/api/download/orderresult/${orderDetails?.id}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                textDecoration: "none",
                                            }}
                                        >
                                            <DownloadOutlined
                                                style={{
                                                    fontSize: 18,
                                                    color: "#1890ff",
                                                    cursor: "pointer",
                                                }}
                                            />
                                        </a>
                                    </Tooltip>
                                )}
                            </Space>
                        }
                    />

                    <NameValueRow
                        key="concentration(ng/ul)"
                        Name={"Concentration(ng/ul)"}
                        Value={orderDetails?.concentration}
                    />
                    <NameValueRow 
                        key="itrpurityanalysis" 
                        Name={"ITR purity analysis"} 
                        Value={
                            <Space direction="horizontal" size="small" style={{ alignItems: "center" }}>
                                <Switch
                                    checked={orderDetails?.itrPurityAnalysis || false}
                                    onChange={async (checked) => {
                                        if (!orderDetails?.id) return
                                        try {
                                            const result = await updateOrderItrPurityAnalysis({
                                                orderId: orderDetails.id,
                                                itrPurityAnalysis: checked
                                            })
                                            if (result.code === 1000) {
                                                messageApi.success(`ITR purity analysis ${checked ? 'enabled' : 'disabled'}`)
                                                // Refresh the order data
                                                queryClient.invalidateQueries({
                                                    queryKey: ["getorders", "one", orderId],
                                                })
                                            } else {
                                                messageApi.error(result.message || "Failed to update ITR purity analysis status")
                                            }
                                        } catch (error) {
                                            console.error("Error updating ITR purity analysis status:", error)
                                            messageApi.error("Failed to update ITR purity analysis status")
                                        }
                                    }}
                                    checkedChildren="Yes"
                                    unCheckedChildren="No"
                                />
                                <span style={{ 
                                    color: orderDetails?.itrPurityAnalysis ? '#52c41a' : '#ff4d4f',
                                    fontWeight: 500,
                                    marginLeft: 8
                                }}>
                                    {orderDetails?.itrPurityAnalysis ? 'Yes' : 'No'}
                                </span>
                            </Space>
                        } 
                    />
                    <NameValueRow 
                        key="linearized" 
                        Name={"Linearized"} 
                        Value={
                            <span style={{ 
                                color: orderDetails?.linearized ? '#52c41a' : '#ff4d4f',
                                fontWeight: 500
                            }}>
                                {orderDetails?.linearized ? 'Yes' : 'No'}
                            </span>
                        } 
                    />
                    <NameValueRow 
                        key="colonies" 
                        Name={"Colonies Sequencing"} 
                        Value={
                            <span style={{ 
                                color: orderDetails?.colonies ? '#52c41a' : '#ff4d4f',
                                fontWeight: 500
                            }}>
                                {orderDetails?.colonies ? 'Yes' : 'No'}
                            </span>
                        } 
                    />
                    <NameValueRow 
                        key="unpurified" 
                        Name={"Unpurified"} 
                        Value={
                            <span style={{ 
                                color: orderDetails?.unpurified ? '#52c41a' : '#ff4d4f',
                                fontWeight: 500
                            }}>
                                {orderDetails?.unpurified ? 'Yes' : 'No'}
                            </span>
                        } 
                    />
                    <NameValueRow key="shipping" Name={"Shipping"} Value={shippingDetails} />

                    {isAdmin(user.user) && (
                        <>
                            <NameValueRow
                                key="paymentmethod"
                                Name={"Payment method"}
                                Value={orderDetails?.paymentInfo?.method}
                            />
                            {orderDetails?.paymentInfo?.method === "PO" && (
                                <NameValueRow key="paymentinfo" Name={"PO number"} Value={orderDetails?.paymentInfo.PoNumber} />
                            )}
                        </>
                    )}
                    {orderDetails?.promotionCode && (
                        <NameValueRow key="promotioncode" Name={"Promotion code"} Value={orderDetails?.promotionCode} />
                    )}
                    {isAdmin(user.user) && (
                        <NameValueRow key="totalprice" Name={"Total price"} Value={"$"+orderDetails?.totalPrice?.price} />
                    )}
                    {isAdmin(user.user) && (
                        <NameValueRow 
                            key="paid" 
                            Name={"Paid"} 
                        Value={
                            <Space direction="horizontal" size="small" style={{ alignItems: "center" }}>
                                <Switch
                                    checked={orderDetails?.paymentInfo?.paid || false}
                                    onChange={async (checked) => {
                                        if (!orderDetails?.id) return
                                        try {
                                            const result = await updateOrderPaidStatus({
                                                orderId: orderDetails.id,
                                                paid: checked
                                            })
                                            if (result.code === 1000) {
                                                messageApi.success(`Order marked as ${checked ? 'paid' : 'unpaid'}`)
                                                // Refresh the order data
                                                queryClient.invalidateQueries({
                                                    queryKey: ["getorders", "one", orderId],
                                                })
                                            } else {
                                                messageApi.error(result.message || "Failed to update payment status")
                                            }
                                        } catch (error) {
                                            console.error("Error updating payment status:", error)
                                            messageApi.error("Failed to update payment status")
                                        }
                                    }}
                                    checkedChildren="Paid"
                                    unCheckedChildren="Unpaid"
                                />
                                <span style={{ 
                                    color: orderDetails?.paymentInfo?.paid ? '#52c41a' : '#ff4d4f',
                                    fontWeight: 500,
                                    marginLeft: 8
                                }}>
                                    {orderDetails?.paymentInfo?.paid ? 'Yes' : 'No'}
                                </span>
                            </Space>
                        } 
                        />
                    )}
                    
                    {/* Generate Invoice Button - Show only for paid credit card orders without existing invoice */}
                    {isAdmin(user.user) && orderDetails && 
                     orderDetails.paymentInfo?.method === "credit card" && 
                     orderDetails.paymentInfo?.paid && 
                     !orderDetails.invoiceId && (
                        <NameValueRow 
                            key="generateinvoice" 
                            Name={"Invoice"} 
                            Value={
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        if (!orderDetails?.id) return
                                        try {
                                            // Create invoice with user information from the order
                                            const result = await createInvoice({
                                                orderIds: [orderDetails.id],
                                                invoiceDate: new Date().toISOString().split('T')[0],
                                                dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
                                                billingContact: `${orderDetails.user.firstName} ${orderDetails.user.lastName}`,
                                                billingEmail: orderDetails.user.email,
                                                billingAddress: 'TBD', // Backend will populate from user profile
                                                institution: 'TBD' // Backend will populate from user profile
                                            })
                                            
                                            if (result.code === 1000) {
                                                messageApi.success("Invoice generated successfully!")
                                                // Refresh the order data to show updated invoice status
                                                queryClient.invalidateQueries({
                                                    queryKey: ["getorders", "one", orderId],
                                                })
                                            } else {
                                                messageApi.error(result.message || "Failed to generate invoice")
                                            }
                                        } catch (error) {
                                            console.error("Error generating invoice:", error)
                                            messageApi.error("Failed to generate invoice")
                                        }
                                    }}
                                >
                                    📄 Generate Invoice and PDF
                                </Button>
                            } 
                        />
                    )}
                    
                    {/* Download Invoice Button - Show when invoice exists */}
                    {isAdmin(user.user) && orderDetails && orderDetails.invoiceId && (
                        <NameValueRow 
                            key="downloadinvoice" 
                            Name={"Invoice"} 
                            Value={
                                <Button
                                    type="default"
                                    onClick={() => {
                                        if (!orderDetails?.id) return
                                        const url = `/api/download/invoice/order/${orderDetails.id}?r=${Math.random()}`
                                        window.open(url, '_blank')
                                    }}
                                >
                                    📄 Download Invoice PDF
                                </Button>
                            } 
                        />
                    )}
                    <NameValueRow key="specialnotes" Name={"Special notes"} Value={orderDetails?.specialNotes} />
                    <NameValueRow
                        key="orderdate"
                        Name={"Order date"}
                        Value={new Date(orderDetails?.ts ?? "").toLocaleString()}
                    />
                    <NameValueRow
                        key="lastviewedbyadmin"
                        Name={"Last Viewed by Admin"}
                        Value={new Date(orderDetails?.lastViewedByAdmin ?? "").toLocaleString()}
                    />
                    <NameValueRow
                        key="lastviewedbycustomer"
                        Name={"Last Viewed by Customer"}
                        Value={new Date(orderDetails?.lastViewedByCustomer ?? "").toLocaleString()}
                    />

                    <NameValueRow
                        key="resultdownloadlogs"
                        Name={"Result Zip Download Logs"}
                        Value={
                            <div style={{ width: "100%" }}>
                                {isDownloadLogPending ? (
                                    <Skeleton active paragraph={{ rows: 2 }} />
                                ) : downloadLogData?.code === 0 && downloadLogData.downloads?.length > 0 ? (
                                    <Table
                                        dataSource={downloadLogData.downloads}
                                        size="small"
                                        pagination={false}
                                        rowKey="id"
                                        columns={[
                                            {
                                                title: "User",
                                                dataIndex: "username",
                                                key: "username",
                                            },
                                            {
                                                title: "Time",
                                                dataIndex: "ts",
                                                key: "ts",
                                                render: (ts: string) => new Date(ts).toLocaleString(),
                                            },
                                        ]}
                                        style={{ marginTop: 8 }}
                                    />
                                ) : (
                                    <span style={{ color: "#bbb" }}>No downloads</span>
                                )}
                            </div>
                        }
                    />

                    <NameValueRow
                        key="plates"
                        Name="Plates"
                        Value={
                            orderDetails?.samples ? (
                                (() => {
                                    const plateIdSet = new Set<number>()
                                    orderDetails.samples.forEach((sample) => {
                                        sample.wells?.forEach((well) => {
                                            if (well.plateId) plateIdSet.add(well.plateId)
                                        })
                                    })
                                    const plateIds = Array.from(plateIdSet)
                                    return plateIds.length > 0 ? (
                                        <>
                                            {plateIds.map((id, i) => (
                                                <span key={id}>
                                                    <Link to={`/admin/plate/${id}`}>{id}</Link>
                                                    {i < plateIds.length - 1 && ", "}
                                                </span>
                                            ))}
                                        </>
                                    ) : (
                                        <span style={{ color: "#bbb" }}>None</span>
                                    )
                                })()
                            ) : (
                                <span style={{ color: "#bbb" }}>None</span>
                            )
                        }
                    />
                    <NameValueRow
                        key="runs"
                        Name="Runs"
                        Value={
                            orderDetails?.samples ? (
                                (() => {
                                    const runIdSet = new Set<number>()
                                    orderDetails.samples.forEach((sample) => {
                                        sample.wells?.forEach((well) => {
                                            if (well.runId) runIdSet.add(well.runId)
                                        })
                                    })
                                    const runIds = Array.from(runIdSet)
                                    return runIds.length > 0 ? (
                                        <>
                                            {runIds.map((id, i) => (
                                                <span key={id}>
                                                    <Link to={`/admin/run/${id}`}>{id}</Link>
                                                    {i < runIds.length - 1 && ", "}
                                                </span>
                                            ))}
                                        </>
                                    ) : (
                                        <span style={{ color: "#bbb" }}>None</span>
                                    )
                                })()
                            ) : (
                                <span style={{ color: "#bbb" }}>None</span>
                            )
                        }
                    />
                    <NameValueRow
                        key="ordercomments"
                        Name={"Questions and feedback"}
                        Value={
                            <OrderComments
                                comments={orderDetails?.comments ?? []}
                                admin
                                onAddComment={async ({
                                    content,
                                    type,
                                }: {
                                    content: string
                                    type: OrderCommentType
                                }) => {
                                    if (!orderDetails?.id) return
                                    await addOrderComment({ orderId: orderDetails.id, content, type })
                                    // Refetch order details to refresh comments
                                    await queryClient.invalidateQueries({
                                        queryKey: ["getorders", "one", orderId],
                                    })
                                }}
                            />
                        }
                    />

                    {/* Plate Map Display */}
                    {orderDetails?.samples && orderDetails.samples.length > 0 && 
                     (orderDetails.submission_type === 'plate-column-order' || orderDetails.submission_type === 'plate-row-order') && (() => {
                        const totalSamples = orderDetails.samples.length
                        const numPlates = Math.ceil(totalSamples / 96)
                        const plateOrder = orderDetails.submission_type === 'plate-row-order' ? 'row' : 'column'
                        
                        return (
                            <div style={{ marginTop: "1em", marginBottom: "1em" }}>
                                <div style={{ fontWeight: "bold", marginBottom: "8px", color: theme.primary }}>
                                    Plate Layout ({totalSamples} samples, {numPlates} plate{numPlates > 1 ? 's' : ''}):
                                </div>
                                
                                {Array.from({ length: numPlates }).map((_, plateIndex) => {
                                    const startSampleIndex = plateIndex * 96
                                    const endSampleIndex = Math.min(startSampleIndex + 96, totalSamples)
                                    const samplesInPlate = endSampleIndex - startSampleIndex
                                    
                                    return (
                                        <div key={`plate-${plateIndex}`} style={{ marginBottom: numPlates > 1 ? "24px" : "0" }}>
                                            {numPlates > 1 && (
                                                <div style={{ 
                                                    fontWeight: "600", 
                                                    marginBottom: "8px", 
                                                    color: "#388087", 
                                                    fontSize: "13px" 
                                                }}>
                                                    Plate {plateIndex + 1} ({samplesInPlate} samples)
                                                </div>
                                            )}
                                            <div style={{ 
                                                display: "grid", 
                                                gridTemplateColumns: "24px repeat(12, minmax(45px, 1fr))", 
                                                gap: "2px",
                                                fontSize: "9px",
                                                backgroundColor: "#f5f5f5",
                                                padding: "6px",
                                                borderRadius: "4px",
                                                border: "1px solid #d9d9d9",
                                                maxWidth: "800px"
                                            }}>
                                                {/* Column headers */}
                                                <div style={{ fontWeight: "bold", textAlign: "center" }}></div>
                                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(col => (
                                                    <div key={`col-${col}`} style={{ fontWeight: "bold", textAlign: "center", padding: "2px", fontSize: "9px" }}>
                                                        {col}
                                                    </div>
                                                ))}
                                                
                                                {/* Plate wells */}
                                                {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'].map((row, rowIndex) => (
                                                    <Fragment key={`row-${row}`}>
                                                        {/* Row label */}
                                                        <div key={`row-${row}`} style={{ fontWeight: "bold", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                            {row}
                                                        </div>
                                                        
                                                        {/* Wells in this row */}
                                                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((col, colIndex) => {
                                                            let sampleIndexInPlate = -1
                                                            
                                                            // Calculate sample index based on order type
                                                            if (plateOrder === 'row') {
                                                                // Row order: A1, A2, A3, ..., A12, B1, B2, ...
                                                                sampleIndexInPlate = rowIndex * 12 + colIndex
                                                            } else {
                                                                // Column order (default): A1, B1, C1, ..., H1, A2, B2, ...
                                                                sampleIndexInPlate = colIndex * 8 + rowIndex
                                                            }
                                                            
                                                            const globalSampleIndex = startSampleIndex + sampleIndexInPlate
                                                            const sample = orderDetails!.samples[globalSampleIndex]
                                                            const hasSample = sample !== undefined
                                                            
                                                            return (
                                                                <div
                                                                    key={`${row}${col}`}
                                                                    style={{
                                                                        minHeight: "38px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        backgroundColor: hasSample ? "#388087" : "white",
                                                                        color: hasSample ? "white" : "#999",
                                                                        border: "1px solid #ccc",
                                                                        borderRadius: "2px",
                                                                        fontSize: "9px",
                                                                        lineHeight: "1.2",
                                                                        cursor: "default",
                                                                        fontWeight: hasSample ? "500" : "normal",
                                                                        overflow: "hidden",
                                                                        wordBreak: "break-word",
                                                                        padding: "3px",
                                                                        textAlign: "center"
                                                                    }}
                                                                    title={hasSample ? sample.sampleName : ''}
                                                                >
                                                                    {hasSample ? sample.sampleName : ""}
                                                                </div>
                                                            )
                                                        })}
                                                    </Fragment>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    })()}

                    <div
                        style={{
                            marginTop: "1em",
                            fontWeight: "bold",
                            color: theme.primary,
                        }}
                    >
                        Samples:
                    </div>
                    {orderDetails?.samples?.map((s, index) => {
                        return <ViewSampleSequence key={index} sample={s} admin />
                    })}

                    <Button
                        danger
                        disabled={orderDetails?.status === "canceled"}
                        style={{ marginTop: "2em", marginBottom: "2em" }}
                        onClick={async () => {
                            Modal.confirm({
                                title: "Do you really want to cancel this order?",
                                icon: <ExclamationCircleFilled />,
                                cancelText: "No",
                                okText: "Yes, cancel it",
                                content: "You can only cancel orders that have not been processed.",
                                async onOk() {
                                    const result = await updateOrderStatus({
                                        orderId: parseInt(orderId ?? "0"),
                                        newStatus: "canceled",
                                    })
                                    if (result.code === 1000) {
                                        queryClient.invalidateQueries({ queryKey: ["getorders", "one", orderId] })

                                        messageApi.success({
                                            content: `You have successfully canceled order ${orderId}`,
                                        })
                                    } else {
                                        messageApi.error({ content: result.message })
                                    }
                                },
                            })
                        }}
                    >
                        Cancel order
                    </Button>
                </LabeledBox>

                <div
                    style={{
                        margin: "1em 0",
                        padding: "0.75em 1em",
                        background: "#fffbe6",
                        border: "1px solid #ffe58f",
                        borderRadius: 6,
                        color: "#ad8b00",
                        fontWeight: 500,
                    }}
                >
                    <b>Admin Notes:</b> Admin can cancel any order. Be careful.
                </div>

                {isAdmin(user.user) && (
                    <>
                        <div style={{ marginBottom: "6em", marginTop: "2em" }}>
                            <div>Sample status colors:</div>
                            <Space size="small" style={{ marginLeft: 8 }}>
                                {Object.entries(SAMPLE_STATUS_COLORS).map(([status, color]) => (
                                    <span key={status} style={{ display: "inline-block", textAlign: "center" }}>
                                        <div
                                            style={{
                                                width: 100,
                                                height: 60,
                                                background: color,
                                                border: "1px solid #ccc",
                                                borderRadius: 4,
                                                display: "inline-block",
                                                marginBottom: 2,
                                            }}
                                        >
                                            {status ?? "Unknown"}
                                        </div>
                                        <div
                                            style={{
                                                width: 100,
                                                height: 60,
                                                fontWeight: "bold",
                                                color: color,
                                                borderRadius: 4,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginBottom: 2,
                                                fontSize: 16,
                                                writingMode: "vertical-rl",
                                                textOrientation: "mixed",
                                                border: "1px",
                                                paddingTop: 48,
                                            }}
                                        >
                                            {color ?? "Unknown"}
                                        </div>
                                    </span>
                                ))}
                            </Space>
                        </div>
                        <div style={{ marginBottom: "6em", marginTop: "2em" }}>
                            <div>Order status colors:</div>
                            <Space size="small" style={{ marginLeft: 8 }}>
                                {Object.entries(ORDER_STATUS_COLORS).map(([status, color]) => (
                                    <span key={status} style={{ display: "inline-block", textAlign: "center" }}>
                                        <div
                                            style={{
                                                width: 100,
                                                height: 60,
                                                background: color,
                                                border: "1px solid #ccc",
                                                borderRadius: 4,
                                                display: "inline-block",
                                                marginBottom: 2,
                                            }}
                                        >
                                            {status ?? "Unknown"}
                                        </div>
                                        <div
                                            style={{
                                                width: 100,
                                                height: 60,
                                                fontWeight: "bold",
                                                color: color,
                                                borderRadius: 4,
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                marginBottom: 2,
                                                fontSize: 16,
                                                writingMode: "vertical-rl",
                                                textOrientation: "mixed",
                                                border: "1px",
                                                paddingTop: 48,
                                            }}
                                        >
                                            {color ?? "Unknown"}
                                        </div>
                                    </span>
                                ))}
                            </Space>
                        </div>
                    </>
                )}
            </ContainerFull>
        </>
    )
}
